/**
 * STITCH-2-ASSIGN-AND-EXPERIENCE-V1 -- the ONE role-aware Lead quick-view
 * drawer (the benchmark call-pop pattern in AdoraCare's own language).
 *
 * Mounted from the leakage queue, the Workboard, the Leads pages, the Today
 * follow-ups, and the queue-health inline drill. Content comes from the
 * SAME view model every surface builds from the read it already holds
 * (lead-quickview-view.ts -- one truth, seam recorded); actions render ONLY
 * from server-declared authority:
 *   - the assignment strip is the EXISTING LeadOwnerControls (claim /
 *     assign / release over the /me assignment fragment -- leadership gets
 *     the eligible-staff picker, operators get the claim path as "Assign to
 *     me", everyone else gets nothing);
 *   - stage actions arrive through the `stageStrip` slot (SCOPE 5);
 *   - a caller with no live action sees the honest read-only footer.
 * PHI LAW: no phone on this surface ever -- the audited reveal lives ONLY on
 * the canonical full page ("Open full view"); zero clinical content exists
 * on either backing read.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import {
  assignLead as apiAssignLead,
  claimLead as apiClaimLead,
  fetchAssignableStaff,
  releaseLead as apiReleaseLead,
} from '@/dashboard/api';
import type { DashboardMe } from '@/dashboard/types';
import {
  AcBadge,
  AcLink,
  Detail,
  DrawerShell,
  EmptyState,
  ReferenceRegion,
  WorkRegion,
} from '@/dashboard/ui';
import LeadOwnerControls, { OWNER_POOL_VALUE } from '@/dashboard/leads/LeadOwnerControls';
import { formatTimestampIst } from '@/dashboard/owner/Lead360Panel';
import { humanizeDispositionSlug } from '@/dashboard/leads/last-contact-attempt';
import {
  quickViewFullHref,
  type LeadQuickViewModel,
} from '@/dashboard/leads/lead-quickview-view';

export const QUICKVIEW_READONLY_COPY = 'Read-only view for your role.';
export const QUICKVIEW_OPEN_FULL_COPY = 'Open full view';
export const QUICKVIEW_UNCLAIMED_COPY = OWNER_POOL_VALUE;

export interface LeadQuickViewProps {
  readonly model: LeadQuickViewModel;
  /** The signed-in caller's /me payload (null => read-only strip). */
  readonly me: DashboardMe | null;
  readonly onClose: () => void;
  /** Hosting list refetch after a successful owner change. */
  readonly onChanged?: () => void;
  /** SCOPE 5 stage-aware action strip (already authority-filtered). */
  readonly stageStrip?: ReactNode;
  /**
   * CALL-CENTER-LEAD-360-SALESFORCE-EXPERIENCE-V1 (Mission 10): the ONE
   * disposition panel (leads/LeadDispositionPanel over the SAME
   * POST /api/leads/:id/disposition writer), injected by hosts whose
   * caller lawfully holds the workspace write. OMITTED => absent
   * (byte-identical drawer; never a dead door). The writer is NEVER
   * duplicated here -- this is a render slot only.
   */
  readonly dispositionPanel?: ReactNode;
  /** Extra content the hosting surface wants under the facts (optional). */
  readonly children?: ReactNode;
  /**
   * ADORACARE-VISUAL-SYSTEM-V1 -- the FRAME the same content renders in.
   * 'drawer' (default; every existing host renders byte-identically) = the
   * DrawerShell overlay, for a peek the operator asked for. 'panel' = the
   * persistent, non-blocking context panel docked inside a work plane
   * (.ac-panel: the same head / body / foot anatomy as the drawer, no
   * scrim, no Close): a detail you are BROWSING is never a modal over the
   * list. In the panel the two head facts (stage, clinic) ride the head
   * and the NEXT sentence + the disposition act come FIRST in DOM order --
   * the ONE obvious action before the reading -- so no CSS reordering is
   * needed and tab order equals visual order. ONE content truth, two
   * frames; the writer and the masking are untouched.
   */
  readonly frame?: LeadQuickViewFrame;
  /** A preview keeps all reads but omits owner, stage and contact writers. */
  readonly contextOnly?: boolean;
}

export type LeadQuickViewFrame = 'drawer' | 'panel';

function FactRow({ label, value }: { label: string; value: ReactNode }): JSX.Element | null {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="lead-quickview__fact">
      <span className="lead-quickview__fact-label">{label}</span>
      <span className="lead-quickview__fact-value">{value}</span>
    </div>
  );
}

export default function LeadQuickView({
  model,
  me,
  onClose,
  onChanged,
  stageStrip,
  dispositionPanel,
  children,
  frame = 'drawer',
  contextOnly = false,
}: LeadQuickViewProps): JSX.Element {
  const panelFrame = frame === 'panel';
  const fullHref = quickViewFullHref(model);
  // The canonical page admits only the leads-workspace audience; everyone
  // else keeps the drawer as their read (doctrine 06 aggregate read-down).
  const showFullLink = fullHref !== null && me?.leadsWorkspaceEnabled === true;
  const canHostOwnerControls =
    !contextOnly &&
    model.leadId !== null &&
    model.ownership !== null &&
    me !== null &&
    me.leadAssignmentEnabled === true;

  const footer = (
    <>
      {contextOnly ? null : stageStrip ?? null}
      {showFullLink ? (
        <Link
          to={fullHref}
          className="ac-btn ac-btn--secondary ac-btn--sm"
          data-testid="quickview-open-full"
        >
          {QUICKVIEW_OPEN_FULL_COPY}
        </Link>
      ) : null}
      {!canHostOwnerControls && (contextOnly || !stageStrip) ? (
        <span className="lead-quickview__readonly" data-testid="quickview-readonly">
          {contextOnly ? 'Read-only preview.' : QUICKVIEW_READONLY_COPY}
        </span>
      ) : null}
    </>
  );

  const facts = (
    <div className="lead-quickview__facts" data-testid="quickview-facts">
      {/* In the panel frame the clinic and the stage ride the head (T2 name
          + meta), so they are NOT repeated here -- say it once. */}
      {panelFrame ? null : <FactRow label="Clinic" value={model.clinicLabel} />}
      {panelFrame ? null : (
        <FactRow
          label="CRM stage"
          value={
            model.stageLabel !== null ? (
              <AcBadge tone="brand">{model.stageLabel}</AcBadge>
            ) : null
          }
        />
      )}
      <FactRow label="Waiting" value={model.waitingLabel} />
      <FactRow label="Source" value={model.sourceLabel} />
      <FactRow label="Captured" value={model.capturedAtLabel} />
      <FactRow label="Appointment" value={model.appointmentLabel} />
      {/* RECORD-360-WORKSPACE-V1: the SAME current-truth contract the
          full page renders (record/current-truth.ts) -- drawer state ===
          page state by construction; depth may differ, state may not. */}
      <FactRow
        label="Outcome"
        value={
          model.conversionKind !== null && model.conversionKind !== 'none' ? (
            <AcBadge
              tone={
                model.conversionKind === 'not_converted' ||
                model.conversionKind === 'contradiction'
                  ? 'danger'
                  : model.conversionKind === 'converted'
                    ? 'positive'
                    : 'info'
              }
              testId="quickview-conversion"
            >
              {model.conversionLabel}
            </AcBadge>
          ) : null
        }
      />
      <FactRow
        label="Payment"
        value={
          model.paymentKind !== null &&
          model.paymentKind !== 'none' &&
          model.paymentKind !== 'unknown_on_this_read' ? (
            <AcBadge
              tone={
                model.paymentKind === 'recorded'
                  ? 'positive'
                  : model.paymentKind === 'collect_pending'
                    ? 'pending'
                    : 'info'
              }
              testId="quickview-payment"
            >
              {model.paymentLabel}
            </AcBadge>
          ) : null
        }
      />
      <FactRow
        label="Follow-up"
        value={
          model.followUpLabel !== null ? (
            <span data-testid="quickview-followup">{model.followUpLabel}</span>
          ) : null
        }
      />
      <FactRow
        label="Patient record"
        value={
          model.patientLinked ? (
            <AcBadge tone="positive">Linked</AcBadge>
          ) : model.existingPatientMatch ? (
            // RETURNING SIGNAL: the unlinked lead's phone matches an
            // existing patient (id/UMR on the record, never a phone);
            // the link action lives on the canonical page's card.
            <AcBadge tone="brand">Existing patient</AcBadge>
          ) : null
        }
      />
      {model.historicalWarningLabels.length > 0 ? (
        <p
          className="lead-quickview__readonly"
          data-testid="quickview-historical"
        >
          {model.historicalWarningLabels[0]}
        </p>
      ) : null}
    </div>
  );

  const next =
    model.nextAction !== null ? (
      <p className="lead-quickview__next" data-testid="quickview-next-action">
        <span className="lead-quickview__next-label">Next best action</span>
        {model.nextAction}
      </p>
    ) : null;

  // Mission 10 -- the SAME last-attempt truth the full record renders
  // (labeled fields; agent-recorded evidence honestly labeled).
  const lastAttempt =
    model.lastAttempt !== null ? (
      <div
        className="lead-quickview__last-attempt"
        data-testid="quickview-last-attempt"
      >
        <span className="lead-quickview__block-label">Last contact attempt</span>
        <FactRow
          label="When"
          value={formatTimestampIst(model.lastAttempt.atIso)}
        />
        <FactRow
          label="Channel"
          value={humanizeDispositionSlug(model.lastAttempt.channel)}
        />
        <FactRow
          label="Result"
          value={humanizeDispositionSlug(model.lastAttempt.resultId)}
        />
        {model.lastAttempt.callbackAtIso !== null ? (
          <FactRow
            label="Callback due"
            value={formatTimestampIst(model.lastAttempt.callbackAtIso)}
          />
        ) : null}
        <FactRow
          label="Evidence"
          value={
            <span
              className="ac-tag"
              data-testid="quickview-last-attempt-evidence"
            >
              Agent-recorded
            </span>
          }
        />
      </div>
    ) : null;

  // Mission 10 -- the ONE disposition panel, rendered in place when the
  // host injected it (the writer stays the merged core's endpoint).
  const disposition = contextOnly ? null : dispositionPanel ?? null;

  const owner =
    model.ownership !== null ? (
      canHostOwnerControls && model.leadId !== null ? (
        <LeadOwnerControls
          leadId={model.leadId}
          ownership={model.ownership}
          me={me}
          claim={(id) => apiClaimLead(id)}
          assign={(id, target) => apiAssignLead(id, target)}
          release={(id) => apiReleaseLead(id)}
          loadAssignableStaff={fetchAssignableStaff}
          onChanged={() => onChanged?.()}
        />
      ) : (
        <div className="lead-quickview__fact" data-testid="quickview-owner-static">
          <span className="lead-quickview__fact-label">Owner</span>
          <span className="lead-quickview__fact-value">
            {model.ownership.assigned ? (
              <AcBadge tone="brand">
                {model.ownership.ownerLabel ?? 'Assigned'}
              </AcBadge>
            ) : (
              <AcBadge tone="pool">{QUICKVIEW_UNCLAIMED_COPY}</AcBadge>
            )}
          </span>
        </div>
      )
    ) : null;

  const activity = (
    <div className="lead-quickview__activity" data-testid="quickview-activity">
      <span className="lead-quickview__block-label">Latest activity</span>
      {model.activity.length === 0 ? (
        panelFrame ? (
          // In the persistent panel an absent history is a quiet line, not
          // a boxed empty state -- it is not a decision the operator makes.
          <p className="lead-quickview__readonly" data-testid="quickview-activity-none">
            No recorded activity yet.
          </p>
        ) : (
          <EmptyState title="No recorded activity yet." />
        )
      ) : (
        <ol className="lead-quickview__events">
          {model.activity.map((e, idx) => (
            <li key={`${e.whenLabel ?? 'na'}-${idx}`} className="lead-quickview__event">
              <span className="lead-quickview__event-label">{e.label}</span>
              {e.whenLabel !== null ? (
                <span className="lead-quickview__event-when">{e.whenLabel}</span>
              ) : null}
            </li>
          ))}
        </ol>
      )}
    </div>
  );

  if (panelFrame) {
    // THE PANEL FRAME (MY-WORK-PRIMITIVE-CONFORMANCE-V6): the Detail card of
    // the master-detail primitive -- head (the name as an ink link to the
    // full record where the audience may go there, the stage chip, the
    // clinic), body = the WORK region (NEXT as its title, the act under it)
    // over the REFERENCE regions (facts, last attempt, owner, the host's
    // note; then the activity as a timeline), foot = the full-record link.
    // Same content, same order within each region as the drawer.
    const head = (
      <>
        <h2 className="ac-detail__title" data-testid="quickview-title">
          {showFullLink ? (
            <AcLink to={fullHref} tone="ink" testId="quickview-title-link">
              {model.title}
            </AcLink>
          ) : (
            model.title
          )}
        </h2>
        {model.stageLabel !== null || model.clinicLabel !== null ? (
          <div className="ac-detail__meta">
            {model.stageLabel !== null ? (
              <AcBadge tone="brand" testId="quickview-stage">
                {model.stageLabel}
              </AcBadge>
            ) : null}
            {model.clinicLabel !== null ? (
              <span data-testid="quickview-clinic">{model.clinicLabel}</span>
            ) : null}
          </div>
        ) : null}
      </>
    );
    const panelFoot = (
      <>
        {contextOnly ? null : stageStrip ?? null}
        {showFullLink ? (
          <AcLink to={fullHref} testId="quickview-open-full">
            {QUICKVIEW_OPEN_FULL_COPY}
          </AcLink>
        ) : null}
        <span className="ac-detail__spacer" aria-hidden="true" />
        {!canHostOwnerControls && (contextOnly || !stageStrip) ? (
          <span className="ac-detail__muted" data-testid="quickview-readonly">
            {contextOnly ? 'Read-only preview.' : QUICKVIEW_READONLY_COPY}
          </span>
        ) : null}
      </>
    );
    return (
      <Detail
        label={`${model.title} context`}
        testId="lead-quickview"
        frame="panel"
        head={head}
        foot={panelFoot}
      >
        <WorkRegion
          eyebrow="Next"
          testId="quickview-work"
          title={
            model.nextAction !== null ? (
              <span data-testid="quickview-next-action">{model.nextAction}</span>
            ) : undefined
          }
        >
          {disposition}
        </WorkRegion>
        <ReferenceRegion eyebrow="Reference" testId="quickview-reference">
          {facts}
          {lastAttempt}
          {owner}
          {children ?? null}
        </ReferenceRegion>
        <ReferenceRegion eyebrow="Recent activity" testId="quickview-activity-region" grow>
          {activity}
        </ReferenceRegion>
      </Detail>
    );
  }

  return (
    <DrawerShell
      eyebrow="Lead quick view"
      title={model.title}
      onClose={onClose}
      footer={footer}
      testId="lead-quickview"
    >
      {facts}
      {next}
      {lastAttempt}
      {disposition}
      {owner}
      {children ?? null}
      {activity}
    </DrawerShell>
  );
}
